import React, { useState, useRef, useEffect, useContext, useCallback, memo } from 'react';
import { Stage, Layer, Transformer, Image, Circle, Arrow, Rect, Text } from 'react-konva';
import Konva from 'konva'
import Context from "../../../utils/Context"
import useImage from 'use-image'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSave, faCircle } from "@fortawesome/free-solid-svg-icons"
import logo from '../../../assets/images/logo.png'
import api from '../../../api/api';
import OccupancyGridVisualizer from "../Map/OccupancyGridVisualizer"
import { ImageUtils } from 'three';

const PointShape = memo(function PointShape({ shape, onPointUp, onTransformStart, onTransformEnd }) {
    return (
        <Circle
            {...shape}
            onMouseUp={onPointUp}
            onTouchEnd={onPointUp}
            onTransformStart={onTransformStart}
            onTransformEnd={onTransformEnd}
        />
    )
})

const PointLabel = memo(function PointLabel({ shape, visible }) {
    return (
        <Text
            visible={visible}
            x={shape.x - 12}
            y={shape.y - 16}
            fontFamily="NeoTechAlt"
            fontSize={10}
            fill='black'
            text={shape.name}
        />
    )
})

const EdgeShape = memo(function EdgeShape({ shape, onEdgeUp }) {
    return (
        <Arrow
            {...shape}
            onMouseUp={onEdgeUp}
            onTouchEnd={onEdgeUp}
        />
    )
})

const ZoneShape = memo(function ZoneShape({ shape, onZoneClick, onDragStart, onDragEnd }) {
    return (
        <Rect
            {...shape}
            onClick={onZoneClick}
            onTouchEnd={onZoneClick}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
        />
    )
})

export function Map(props) {
    const context = useContext(Context)

    const {
        canvasRef,
        openModal,
        updateCanvas,
        robotPos
    } = props

    const [mapImage, setMapImage] = useState('')

    const [mapImageVersion, setMapImageVersion] = useState(0)
    const [stageVisibility, setStageVisibility] = useState(false)
    const [labelVisibility, setLabelVisibility] = useState(false)
    const [pointType, setPointType] = useState([true, false])
    const [edgeType, setEdgeType] = useState([true, false, false])
    const [isDragging, setIsDragging] = useState(false)
    const [pointName, setPointName] = useState('')
    const [lastDist, setLastDist] = useState(0)
    const [clickState, setClickState] = useState(2)
    const [edgeState, setEdgeState] = useState(0)
    const [isNew, setIsNew] = useState(false)
    const [menuPosX, setMenuPosX] = useState(0)
    const [menuPosY, setMenuPosY] = useState(0)
    const [menuOffsetX, setMenuOffsetX] = useState(0)
    const [menuOffsetY, setMenuOffsetY] = useState(0)
    const [repeatCounter, setRepeatCounter] = useState(0)
    //const [edgesInfo, setEdgesInfo] = useState([])
    const [edgePoint, setEdgePoint] = useState({ id: 0, x: 0, y: 0 })
    //const [dragCoords, setDragCoords] = useState({ x: 0, y: 0 })
    const [pointLabel, setPointLabel] = useState({ x: 0, y: 0, name: '' })
    const [selectionBox, setSelectionBox] = useState({ visible: false, x: 0, y: 0, width: 0, height: 0 })
    const [draggable, setDraggable] = useState(false)
    const [preModalValues, setPreModalValues] = useState([])

    const [edgesInfo, setEdgesInfo] = useState({})
    const [dragCoords, setDragCoords] = useState({})

    const layerRef = useRef() // interactive shapes layer (points, edges, zones, transformer, selection rect)
    const imageLayerRef = useRef() // static background map image, own layer so it never redraws with shapes
    const imageRef = useRef()
    const timerRef = useRef()
    const loadingRef = useRef()
    const stageRef = useRef()
    const trRef = useRef(null)
    const selectionRectRef = useRef(null)
    const selecting = useRef(false)
    const stageProps = useRef({ scale: 1, x: 0, y: 0 })
    const multiDragHelper = useRef([])

    let [image, status] = useImage(`${mapImage}`, 'anonymous')

    const deepClone = (obj) => {
        return JSON.parse(JSON.stringify(obj))
    }

    const getCurrentMap = () => {
        if (context.settings.fmsConnection) {
            return context.maps.find(m => m.name == context.selected.map?.name && m.robotId === context.robotInfo._id)
        }

        return context.maps.find(m => m.name === context.selected?.map?.name) || context.maps[0]
    }

    const getResolution = () => {
        const mapDimensionResolution = Number(context.mapDimensions?.resolution)
        if (Number.isFinite(mapDimensionResolution) && mapDimensionResolution > 0) return mapDimensionResolution

        const selectedMapResolution = Number(getCurrentMap()?.resolution)
        if (Number.isFinite(selectedMapResolution) && selectedMapResolution > 0) return selectedMapResolution

        return 1
    }

    useEffect(() => {
        context.actions.setPointCopy([])
    }, [])

    useEffect(() => {
        if (context.pasted.length > 0) {
            unpopShapes()

            const copiedCircles = layerRef.current.getChildren().filter((node) => node.getClassName() === 'Circle' && context.pasted.some(point => point.name === node.attrs.name))

            copiedCircles.forEach((circle) => popShape({ target: circle }))

            context.actions.setSelectedShapes(copiedCircles)
            trRef.current.nodes(copiedCircles)
            setLabelVisibility(false)

            context.actions.setPasted([])
        }
    }, [context.pasted])

    useEffect(() => {
        let updatedEdges = []

        context.konvaPoints && context.konvaPoints.length > 0 && context.konvaPoints.forEach((point, i) => {
            if (i === 0) {
                updatedEdges = context.konvaEdges.map((edge) => {
                    //console.log(edge.pointIds, point.name)
                    if (edge.pointIds[0] === point.name) {
                        return {
                            ...edge,
                            points: [point.x, point.y, edge.points[2], edge.points[3]]
                        }
                    } else if (edge.pointIds[1] === point.name) {
                        return {
                            ...edge,
                            points: [edge.points[0], edge.points[1], point.x, point.y]
                        }
                    } else return edge
                })
            } else {
                updatedEdges = updatedEdges.map((edge) => {
                    //console.log(edge.pointIds, point.name)
                    if (edge.pointIds[0] === point.name) {
                        return {
                            ...edge,
                            points: [point.x, point.y, edge.points[2], edge.points[3]]
                        }
                    } else if (edge.pointIds[1] === point.name) {
                        return {
                            ...edge,
                            points: [edge.points[0], edge.points[1], point.x, point.y]
                        }
                    } else return edge
                })
            }

            //console.log(i, point.name, updatedEdges)
        })
        //console.log(updatedEdges)

        context.actions.setKonvaEdges(updatedEdges)
    }, [context.konvaPoints])

    useEffect(() => {
        if (stageRef.current) {
            stageRef.current.scale({ x: stageProps.current.scale, y: stageProps.current.scale })
            stageRef.current.position({ x: stageProps.current.x, y: stageProps.current.y })
            stageRef.current.batchDraw()
        }
    }, [stageProps.current])

    useEffect(() => {
        console.log(context.mappingStatus)
        if (context.mappingStatus) {
            setMapImage(`http://${context.selectedRobot.ip}/temp_map.png`)
            timerRef.current = setInterval(() => {
                setMapImage('')
                setTimeout(() => {
                    console.log('refetching...')
                    setMapImage(`http://${context.selectedRobot.ip}/temp_map.png?t=${Date.now()}`)
                }, [50])
            }, [5000])
        } else {
            const currentMap = context.maps.find(m => m.name === context.selected?.map?.name)
            clearInterval(timerRef.current)
            if (currentMap) loadImage(currentMap)
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
        }
    }, [context.mappingStatus, context.mappingCurrent])

    useEffect(() => {
        console.log(context.endOfMission, context.mappingStatus)

        if (context.selected.tool?._id === "location" && context.robotPosition) {
            setIsNew(true)
            setPointType([true, false, false])
            document.getElementById('point-modal').classList.add('is-active')
            setPreModalValues([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])
            document.getElementById('pointName').classList.add('is-flex')

            const currentMap = context.maps.find(m => m.name === context.selected.map?.name)

            setMenuPosX(((context.robotPosition.position.x - currentMap.origin.x)).toFixed(6))
            setMenuPosY((-(context.robotPosition.position.y - currentMap.origin.y) + context.mapDimensions.height * currentMap.resolution).toFixed(6))

            const here = {
                radius: 5,
                fill: 'blue',
                x: (context.robotPosition.position.x - currentMap.origin.x) / currentMap.resolution,
                y: -(context.robotPosition.position.y - currentMap.origin.y) / currentMap.resolution + context.mapDimensions.height,
                draggable: 'true',
                name: 'point' + repeatCounter
            }
            //console.log('new point!', isDragging)
            setRepeatCounter(repeatCounter + 1)
            context.actions.setKonvaPoints([...context.konvaPoints, here])

            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [[...context.konvaPoints, here], context.konvaEdges, context.konvaZones] }])
            context.actions.setUndone([])
            context.actions.setSelected({ ...context.selected, tool: { _id: 'points', name: 'Navigation' } })
        }

        if (stageRef.current) {
            if (context.selected.tool?._id === "show") {
                stageRef.current.children[0].children.forEach(child => {
                    console.log(child.getClassName())
                    if (child.getClassName() === 'Circle') popShape({ target: child })
                })
            } else {
                unpopShapes()
            }
        }
    }, [context.selected.tool?._id])

    //ON INIT: HITONDRAGENABLED, OR ELSE MAP BREAKS :)
    useEffect(() => {
        console.log(context.endOfMission, context.lastMission)

        context.actions.setSelector(false)
        let currentMap

        Konva.hitOnDragEnabled = true
        if (context.settings.fmsConnection) {
            currentMap = context.maps.find(m => m.name == context.selected.map?.name && m.robotId === context.robotInfo._id)
        } else {
            currentMap = context.maps[0]
        }

        console.log(context.maps, currentMap)

        if (currentMap?._id) {
            if ((context.lastMission === 'select-map' && context.endOfMission) || context.lastMission !== 'select-map') {
                console.log(currentMap)
                loadImage(currentMap)
                console.log(currentMap.objects)
                if (context.settings.fmsConnection) context.actions.setMapObjects(currentMap.objects)
                clearInterval(loadingRef.current)
            } else {
                loadingRef.current = setInterval(() => {
                    if (document.getElementById('loading').innerHTML !== 'Loading map...') {
                        document.getElementById('loading').innerHTML += '.'
                    } else {
                        document.getElementById('loading').innerHTML = 'Loading map'
                    }
                }, [500])
                setMapImage('')
            }
        } else {
            console.warn('No map selected')
            clearInterval(loadingRef.current)
        }

        return () => {
            if (loadingRef.current) clearInterval(loadingRef.current)
        }
    }, [context.selected.map, context.endOfMission])

    useEffect(() => {
        if (isNew) {
            let id = 0
            let newShape
            layerRef.current.children.forEach((item) => {
                if (item._id > id) {
                    id = item._id
                    newShape = item
                }
            })
            console.log(newShape)
            context.actions.setShapeRef(newShape)
        }
    }, [isNew])

    //LOADS THE MAP
    const loadImage = async (map) => {
        let img = imageRef.current
        //console.log('http://' + context.settings.fmsIp + '/fms' + map.link)
        //setMapImage('http://' + context.settings.fmsIp + '/fms' + map.link)

        updateCanvas(document.getElementById('canvas-id'))

        console.log(map, map.objects)

        const mapUrl = 'http://' + context.selectedRobot.ip + map.link + '?=' + Date.now();
        console.log('Setting Map Image:', mapUrl); // Debug to ensure URL is correct

        setMapImage(mapUrl)
        setMapImageVersion((prev) => prev + 1)
        setStageVisibility(true)

        console.log(img, map, image)
    }

    useEffect(() => {
        if (image) {
            console.log('MAP FEED', context.mapFeedFull)
            let img = imageRef.current
            stageProps.current = { ...stageProps.current, x: (stageRef.current.attrs.width - image.width) / 2, y: (stageRef.current.attrs.height - image.height) / 2 }

            if (img) {
                img.cache()
                img.drawHitFromCache()
            }

            let map
            if (context.settings.fmsConnection) map = context.maps.find(m => m.name === context.selected.map?.name && m.robotId === context.robotInfo._id)
            else map = context.maps[0]
            context.actions.setMapDimensions({ width: img.attrs.image.width, height: img.attrs.image.height, resolution: map ? map.resolution : null })
            //img.position({ x: context.mapFeed?.position[0], y: context.mapFeed?.position[1] })
            //img.scale({ x: context.mapFeed?.scale[0], y: context.mapFeed?.scale[1] })

            console.log(map?.objects)

            if (map?.objects) {
                let allPoints = []
                let allEdges = []
                let allZones = []
                if (map.objects.points) {
                    let importedPoints = []
                    map.objects.points.map((point) => {
                        importedPoints.push({
                            draggable: 'true',
                            fill: 'blue',
                            name: point.name[0],
                            radius: 5,
                            //x: (point.point.x - map.origin.x) / map.resolution,
                            x: (point.point.x - map.origin.x) / map.resolution,
                            //y: (point.point.y - map.origin.y) / map.resolution,
                            y: -(point.point.y - map.origin.y) / map.resolution + img.attrs.image.height
                        })
                    })

                    allPoints = importedPoints
                    //setPoints(importedPoints)
                }

                if (map.objects.edges) {
                    let importedEdges = []
                    map.objects.edges.map((edge) => {
                        const startPoint = allPoints.find((point) => point.name === edge.from)
                        const endPoint = allPoints.find((point) => point.name === edge.to)
                        console.log(startPoint, endPoint)
                        let color = 'blue'
                        let pointer = false

                        if (!edge.avoid) color = 'purple'

                        if (!edge.reversable) pointer = true

                        if (startPoint && endPoint) {
                            importedEdges.push({
                                points: [startPoint.x, startPoint.y, endPoint.x, endPoint.y],
                                stroke: color,
                                hitStrokeWidth: 15,
                                fill: color,
                                opacity: 0.5,
                                name: startPoint.name + ' a ' + endPoint.name,
                                pointerAtEnding: pointer,
                                pointIds: [startPoint.name, endPoint.name]
                            })
                        }
                    })
                    console.log(importedEdges)

                    allEdges = importedEdges
                }

                if (map.objects.no_go) {
                    let nogoPoints = []
                    let nogoEdges = []
                    let previousPoints = []
                    let closedPolygon = false

                    map.objects.no_go.map((zone) => {
                        zone.polygon.map((point) => {
                            if (previousPoints.length === 0 || !previousPoints.includes(point.counter)) {
                                nogoPoints.push({
                                    draggable: 'true',
                                    fill: 'red',
                                    name: point.counter,
                                    radius: 4,
                                    //x: (point.point.x - map.origin.x) / map.resolution,
                                    x: (point.point.x - map.origin.x) / map.resolution,
                                    //y: (point.point.y - map.origin.y) / map.resolution,
                                    y: -(point.point.y - map.origin.y) / map.resolution + img.attrs.image.height
                                })

                                previousPoints.push(point.counter)
                            } else {
                                closedPolygon = true
                            }
                        })

                        previousPoints = []

                        for (let i = 0; i < nogoPoints.length; i++) {
                            let point = nogoPoints[i]
                            let nextPoint
                            if (i === nogoPoints.length - 1) {
                                break
                            } else {
                                nextPoint = nogoPoints[i + 1]
                            }

                            nogoEdges.push({
                                points: [point.x, point.y, nextPoint.x, nextPoint.y],
                                stroke: 'red',
                                hitStrokeWidth: 15,
                                fill: 'red',
                                opacity: 0.5,
                                name: point.name + ' a ' + nextPoint.name,
                                pointerAtEnding: false,
                                pointIds: [point.name, nextPoint.name]
                            })
                        }

                        if (closedPolygon) {
                            closedPolygon = false

                            nogoEdges.push({
                                points: [nogoEdges[nogoEdges.length - 1].points[2], nogoEdges[nogoEdges.length - 1].points[3], nogoEdges[0].points[0], nogoEdges[0].points[1]],
                                stroke: 'red',
                                hitStrokeWidth: 15,
                                fill: 'red',
                                opacity: 0.5,
                                name: nogoEdges[nogoEdges.length - 1].pointIds[1] + ' a ' + nogoEdges[0].pointIds[0],
                                pointerAtEnding: false,
                                pointIds: [nogoEdges[nogoEdges.length - 1].pointIds[1], nogoEdges[0].pointIds[0]]
                            })
                        }

                        allPoints = allPoints.concat(nogoPoints)
                        nogoPoints = []
                    })

                    console.log(nogoPoints)
                    console.log(nogoEdges)

                    allEdges = allEdges.concat(nogoEdges)
                }

                if (map.objects.areas) {
                    let slowZones = []

                    map.objects.areas.map((zone) => {
                        let fillColor, strokeColor = ''
                        console.log(zone)

                        if (!zone.beep) {
                            fillColor = '#FFB47F'
                            strokeColor = 'orange'
                        } else {
                            fillColor = '#FF7FE8'
                            strokeColor = 'hotpink'
                        }
                        slowZones.push({
                            name: zone.name,
                            fill: fillColor,
                            stroke: strokeColor,
                            strokeWidth: 1,
                            opacity: 0.6,
                            x: (zone.polygon[0].point.x - map.origin.x) / map.resolution,
                            y: -(zone.polygon[0].point.y - map.origin.y) / map.resolution + img.attrs.image.height,
                            draggable: 'true',
                            height: -((-(zone.polygon[0].point.y - map.origin.y) / map.resolution + img.attrs.image.height) - (-(zone.polygon[3].point.y - map.origin.y) / map.resolution + img.attrs.image.height)),
                            width: ((zone.polygon[1].point.x - map.origin.x) / map.resolution) - ((zone.polygon[0].point.x - map.origin.x) / map.resolution),
                            rotation: Math.atan2(zone.polygon[1].point.y - zone.polygon[0].point.y, zone.polygon[1].point.x - zone.polygon[0].point.x) * (180 / Math.PI),
                            scaleX: 1,
                            scaleY: 1,
                            velocity: zone.velocity
                        })
                    })

                    console.log(slowZones)

                    allZones = allZones.concat(slowZones)
                }

                console.log(allPoints, allEdges, allZones)

                setRepeatCounter(allPoints.length + allZones.length)
                context.actions.setKonvaPoints(allPoints)
                context.actions.setKonvaEdges(allEdges)
                context.actions.setKonvaZones(allZones)
            } else {
                context.actions.setKonvaPoints([])
                context.actions.setKonvaEdges([])
                context.actions.setKonvaZones([])
            }
        }
    }, [image])

    //HANDLES EVERYTHING THAT HAPPENS ONCE YOU START DRAGGING THE MAP OR ANY SHAPE
    const handleOnDragStart = (e) => {
        //e.evt.preventDefault()
        multiDragHelper.current = []

        unpopShapes()
        context.actions.setSelector(false)
        if (e.target.getClassName() === 'Rect') {
            context.actions.setSelectedRect({ ...e.target.attrs })
        } else if (e.target.getClassName() === 'Transformer') {
            context.actions.setSelectedRect()
        }
        //console.log(document.getElementById('shapeChooser').style.display)

        let center = {
            x: e.target.attrs.x,
            y: e.target.attrs.y
        }

        console.log('HEYHEYHEYHEYHEYHEYHEYHEY', e.target, center)

        setIsDragging(true)
        context.actions.setShapeRef(e.target)

        let pointCoords = dragCoords

        if (e.target.attrs.name) {
            pointCoords[e.target.attrs.name] = center
            setDragCoords(pointCoords)
        }

        //console.log(e.target)

        console.log('--dragging--', e.target.getClassName(), e.target.attrs?.name, dragCoords, pointCoords)
    }

    //HANDLES EVERYTHING THAT HAPPENS AFTER DRAGGING THE MAP OR ANY SHAPE
    const handleOnDragEnd = (e) => {
        //e.evt.preventDefault()

        //console.log(e.target.getStage())
        const center = {
            x: dragCoords[e.target.attrs.name]?.x !== e.target.attrs?.x ? e.target.attrs.x : null,
            y: dragCoords[e.target.attrs.name]?.y !== e.target.attrs?.y ? e.target.attrs.y : null
        }

        if (!center.x && !center.y && e.target.getClassName() === 'Circle') return

        let stage = stageRef.current

        if (e.target.getClassName() === 'Circle') {
            if (context.selectedShapes.length === 0)
                popShape(e)
            //else selectedShapes.forEach((shape) => popShape({ target: shape }))
        } else {
            unpopShapes()
        }

        setIsDragging(false)
        //setEdgeState(0)

        console.log('event type?', e.evt?.type)
        console.log('--dragged--', e.target.getClassName(), e.target.attrs?.name, center)

        const oldScale = stage.scaleX()
        const pos = {
            x: oldScale * center.x + stage.x(),
            y: oldScale * center.y + stage.y()
        }

        const imageHitbox = imageRef.current

        //console.log(dragCoords)
        //console.log(xPos, yPos)
        //console.log(imageHitbox.intersects({x: pos.x, y: pos.y}))

        if (e.target.getClassName() !== 'Stage' && e.target.getClassName() !== 'Transformer') {
            if ((!imageHitbox.intersects({ x: pos.x, y: pos.y }) && e.target.attrs.fill !== 'red' && e.target.attrs.fill !== '#FFB47F' && e.target.getClassName() === 'Circle' && context.selectedShapes < 2) || (!context.endOfMission) || context.selected.tool?._id === 'show') {
                //console.log(pos)

                e.target.x(dragCoords[e.target.attrs.name].x)
                e.target.y(dragCoords[e.target.attrs.name].y)

                unpopShapes()

                const invalidDrag = context.konvaPoints.map((p, i) => {
                    //console.log(e.target.index)

                    if (p.name === e.target.attrs.name) {
                        console.log(i, p, dragCoords[e.target.attrs.name])
                        setPointLabel({ x: dragCoords[e.target.attrs.name].x, y: dragCoords[e.target.attrs.name].y, name: e.target.attrs.name })

                        return {
                            ...p,
                            x: dragCoords[e.target.attrs.name].x,
                            y: dragCoords[e.target.attrs.name].y
                        }
                    } else return p
                })

                //console.log(invalidDrag)

                context.actions.setKonvaPoints(invalidDrag)
            } else {
                switch (e.target.getClassName()) {
                    case 'Circle':
                        if (context.selectedShapes.length < 2) {
                            const newCoords = context.konvaPoints.map((point, i) => {
                                //console.log(e.target.getLayer())

                                if (point.name === e.target.attrs.name) {
                                    //console.log(xPos, yPos)
                                    setPointLabel({ x: center.x, y: center.y, name: e.target.attrs.name })

                                    return {
                                        ...point,
                                        x: center.x,
                                        y: center.y,
                                    }
                                } else return point
                            })

                            console.log('new coords?', newCoords, context.konvaPoints)
                            context.actions.setKonvaPoints(newCoords)

                            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [newCoords, context.konvaEdges, context.konvaZones] }])
                            context.actions.setUndone([])
                        } else {
                            console.log(multiDragHelper.current)
                            const newCoords = context.konvaPoints.map((point, i) => {
                                //console.log(e.target.getLayer())

                                if (point.name === e.target.attrs.name) {
                                    //console.log(xPos, yPos)

                                    return {
                                        ...point,
                                        x: center.x,
                                        y: center.y,
                                    }
                                } else return null
                            })

                            multiDragHelper.current = multiDragHelper.current.concat(newCoords)

                            let deleteNulls = multiDragHelper.current.filter(function (el) {
                                return el != null
                            })

                            console.log('new coords?', multiDragHelper.current, context.konvaPoints)

                            let concatted = context.konvaPoints.concat(deleteNulls)
                            const seen = new Set()
                            context.actions.setKonvaPoints(concatted.slice().reverse().filter(point => {
                                if (seen.has(point.name)) {
                                    return false
                                }
                                seen.add(point.name)
                                return true
                            }))

                            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [deleteNulls, context.konvaEdges, context.konvaZones] }])
                            context.actions.setUndone([])
                        }
                        break

                    case 'Rect':
                        const newZoneCoords = context.konvaZones.map((zone) => {
                            //console.log(e.target.getLayer())

                            if (zone.name === e.target.attrs.name) {
                                //console.log(center)

                                return {
                                    ...zone,
                                    x: center.x,
                                    y: center.y,
                                }
                            } else return zone
                        })

                        context.actions.setKonvaZones(newZoneCoords)

                        context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [context.konvaPoints, context.konvaEdges, newZoneCoords] }])
                        context.actions.setUndone([])
                        break

                    case 'Transformer':
                        console.log(e.target)
                        break

                    default:
                        break
                }
            }
        }
        selecting.current = false
        setSelectionBox((prev) => ({ ...prev, visible: false }))
        context.actions.setSelectedShapes([])
        trRef.current.nodes([])

        //setDragCoords(center)
        setDragCoords({})

        //console.log('dropped', dragCoords.x, dragCoords.y)
        //console.log('dropped', e.target.x(), e.target.y())
        layerRef.current.batchDraw()

        console.log('points:', context.konvaPoints)
        console.log('edges:', context.konvaEdges)
        console.log('zones:', context.konvaZones)
        console.log('dropped!', pos.x, pos.y)
    }

    //CHECKS IF THE CLICK WAS ON THE MAP FOR DROPDOWN TO SHOW
    const mapDetection = (e) => {
        //e.evt.preventDefault()

        e.target.hitStrokeWidth(20)

        setClickState(e.target._id)

        console.log('clicked map!', e.target._id);
    }

    //SCROLL ZOOM HANDLER
    const scrollZoom = (e) => {
        e.evt.preventDefault()
        const stage = e.target.getStage()

        var scaleBy = 1.1
        var oldScale = stage.scaleX()
        var pointer = stage.getPointerPosition()
        var mousePointTo = {
            x: (pointer.x - stage.x()) / oldScale,
            y: (pointer.y - stage.y()) / oldScale
        }

        let direction = e.evt.deltaY > 0 ? 1 : -1

        if (e.evt.ctrlKey) {
            direction = -direction
        }

        var newScale = direction < 0 ? oldScale * scaleBy : oldScale / scaleBy

        var newPos = {
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale
        }

        if (newScale < 0.2) {
            newScale = 0.2
            newPos.x = stageProps.current.x
            newPos.y = stageProps.current.y
        }
        if (newScale > 5) {
            newScale = 5
            newPos.x = stageProps.current.x
            newPos.y = stageProps.current.y
        }

        stageProps.current = {
            scale: newScale,
            x: newPos.x,
            y: newPos.y
        }

        stage.scale({ x: newScale, y: newScale })
        stage.position(newPos)
        stage.batchDraw()
    }

    //TOUCH ZOOM HANDLER
    const pinchZoom = (e) => {
        //e.evt.preventDefault()
        const stage = e.target.getStage()

        var touch1 = e.evt.touches[0]
        var touch2 = e.evt.touches[1]

        if (touch1 && touch2) {
            setIsDragging(true)
            stage.draggable(false)
            var oldScale = stage.scaleX()

            var pointer1 = {
                x: touch1.clientX - 3,
                y: touch1.clientY - 50
            }
            var pointer2 = {
                x: touch2.clientX - 3,
                y: touch2.clientY - 50
            }

            var center = {
                x: (pointer1.x + pointer2.x) / 2,
                y: (pointer1.y + pointer2.y) / 2
            }

            const dist = Math.sqrt(Math.pow(pointer2.x - pointer1.x, 2) + Math.pow(pointer2.y - pointer1.y, 2))

            if (lastDist !== 0) {
                var newScale = oldScale * (dist / lastDist)
            } else var newScale = oldScale

            var zoomPoint = {
                x: (center.x - stage.x()) / oldScale,
                y: (center.y - stage.y()) / oldScale
            }

            setLastDist(dist)

            var newPos = {
                x: center.x - zoomPoint.x * newScale,
                y: center.y - zoomPoint.y * newScale
            }

            if (newScale < 0.2) {
                newScale = 0.2
                newPos.x = stageProps.current.x
                newPos.y = stageProps.current.y
            }

            if (newScale > 5) {
                newScale = 5
                newPos.x = stageProps.current.x
                newPos.y = stageProps.current.y
            }
            //console.log(Konva.hitOnDragEnabled)

            stageProps.current = {
                scale: newScale,
                x: newPos.x,
                y: newPos.y
            }

            stage.scale({ x: newScale, y: newScale })
            stage.position(newPos)
            stage.batchDraw()
            //console.log('double touch!', stageProps)
        }
    }

    //TOUCH ZOOM RESETTER
    const pinchReset = (e) => {
        if (e.evt.touches.length === 0) {
            setIsDragging(false)
            e.target.getStage().draggable(true)
            setLastDist(0)
        }
    }

    // SETS EDGE CREATION
    const edgeHandler = (e) => {
        //unpopModal()
        if (edgeState === 0) { //início de uma linha
            setEdgePoint({ id: e.target._id, x: e.target.attrs.x, y: e.target.attrs.y, color: e.target.attrs.fill, name: e.target.attrs.name })
            setEdgeState(1)
            console.log(context.shapeRef)
        } else {
            if ((edgePoint.id === e.target._id) || (edgePoint.color !== e.target.attrs.fill)) {
                setEdgeState(0)
                console.log('edge canceled')
            } else {       //fim de uma linha, evitando linha a ser criada com dois cliques no mesmo ponto OU em pontos com cor diferente
                const newEdge = {
                    points: [edgePoint.x, edgePoint.y, e.target.attrs.x, e.target.attrs.y],
                    stroke: edgePoint.color,
                    hitStrokeWidth: 15,
                    fill: edgePoint.color === 'blue' ? 'purple' : edgePoint.color,
                    opacity: 0.5,
                    name: edgePoint.name + ' a ' + e.target.attrs.name,
                    pointerAtEnding: false,
                    pointIds: [edgePoint.name, e.target.attrs.name]
                }

                let isOverlap = false

                context.konvaEdges.map((l) => {
                    console.log(newEdge.pointIds, l.pointIds)
                    if ((newEdge.pointIds[0] == l.pointIds[0] && newEdge.pointIds[1] == l.pointIds[1]) || (newEdge.pointIds[0] == l.pointIds[1] && newEdge.pointIds[1] == l.pointIds[0])) {
                        console.log('overlap!')
                        isOverlap = true
                    }

                    setEdgeState(0)
                    return l
                })

                console.log(isOverlap)

                if (isDragging === false && isOverlap === false) {
                    console.log('new edge!')
                    context.actions.setKonvaEdges([...context.konvaEdges, newEdge])

                    context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [context.konvaPoints, [...context.konvaEdges, newEdge], context.konvaZones] }])
                    context.actions.setUndone([])
                    //console.log(edges)
                }

                setEdgeState(0)
            }
        }
    }

    //POINT CREATOR
    const createPoint = (e) => {
        const oldScale = e.target.getStage().scaleX()
        const resolution = getResolution()
        const mousePointTo = {
            x: e.target.getStage().getPointerPosition().x / oldScale - e.target.getStage().x() / oldScale,
            y: e.target.getStage().getPointerPosition().y / oldScale - e.target.getStage().y() / oldScale
        }

        console.log(e.target._id, clickState, context.pointClickState, isDragging)

        if (((e.target._id === clickState || clickState === 0) && !context.pointClickState && !context.selector && !isDragging && context.endOfMission)) {
            setMenuPosX((mousePointTo.x * resolution).toFixed(4))
            setMenuPosY(((mousePointTo.y - 0.125) * resolution).toFixed(4)) //o y estava a ser calculado com 0.125 a mais

            context.actions.setSelectedRect()
            setIsNew(true)
            setPointType([true, false])
            document.getElementById('point-modal').classList.add('is-active')
            setPreModalValues([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])
            document.getElementById('pointName').classList.add('is-flex')

            const newPoint = {
                radius: 5,
                fill: 'blue',
                x: mousePointTo.x,
                y: mousePointTo.y - 0.125,
                draggable: 'true',
                name: 'point' + repeatCounter
            }
            //console.log('new point!', isDragging)
            setRepeatCounter(repeatCounter + 1)
            setPointName('point' + repeatCounter)
            context.actions.setKonvaPoints([...context.konvaPoints, newPoint])

            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [[...context.konvaPoints, newPoint], context.konvaEdges, context.konvaZones] }])
            context.actions.setUndone([])
        } else if (e.target.getClassName() === 'Arrow' && e.target.attrs.stroke !== 'red' && context.selected.tool?._id === 'points') {
            setMenuPosX((mousePointTo.x * resolution).toFixed(4))
            setMenuPosY(((mousePointTo.y - 0.125) * resolution).toFixed(4)) //o y estava a ser calculado com 0.125 a mais

            context.actions.setSelectedRect()
            setIsNew(true)
            setPointType([true, false])
            document.getElementById('point-modal').classList.add('is-active')
            setPreModalValues([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])
            document.getElementById('pointName').classList.add('is-flex')

            const newPoint = {
                radius: 5,
                fill: 'blue',
                x: mousePointTo.x,
                y: mousePointTo.y - 0.125,
                draggable: 'true',
                name: 'point' + repeatCounter
            }
            //console.log('new point!', isDragging)
            setRepeatCounter(repeatCounter + 1)
            setPointName('point' + repeatCounter)
            context.actions.setKonvaPoints([...context.konvaPoints, newPoint])

            const newEdge1 = {
                points: [e.target.attrs.points[0], e.target.attrs.points[1], mousePointTo.x, mousePointTo.y - 0.125],
                stroke: e.target.attrs.stroke,
                hitStrokeWidth: 15,
                fill: e.target.attrs.fill,
                opacity: 0.5,
                name: e.target.attrs.pointIds[0] + ' a ' + 'point' + repeatCounter,
                pointerAtBeginning: e.target.attrs.pointerAtBeginning,
                pointerAtEnding: e.target.attrs.pointerAtEnding,
                pointIds: [e.target.attrs.pointIds[0], 'point' + repeatCounter]
            }

            const newEdge2 = {
                points: [mousePointTo.x, mousePointTo.y - 0.125, e.target.attrs.points[2], e.target.attrs.points[3]],
                stroke: e.target.attrs.stroke,
                hitStrokeWidth: 15,
                fill: e.target.attrs.fill,
                opacity: 0.5,
                name: 'point' + repeatCounter + ' a ' + e.target.attrs.pointIds[1],
                pointerAtBeginning: e.target.attrs.pointerAtBeginning,
                pointerAtEnding: e.target.attrs.pointerAtEnding,
                pointIds: ['point' + repeatCounter, e.target.attrs.pointIds[1]]
            }

            const deletedEdge = context.konvaEdges.map((edge) => {
                if (edge.name === e.target.attrs.name) {
                    return null
                } else return edge
            })

            let filterDeleted = deletedEdge.filter((point) => {
                return point != null
            })

            context.actions.setKonvaEdges(filterDeleted.concat([newEdge1, newEdge2]))

            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [[...context.konvaPoints, newPoint], filterDeleted.concat([newEdge1, newEdge2]), context.konvaZones] }])
            context.actions.setUndone([])
        } else {
            if (e.target.getClassName() !== 'Circle' && e.target.getClassName() !== 'Rect') {
                unpopShapes()
            }
        }

    }

    //POINT EDITOR
    const editPoint = (e) => {
        //e.evt.preventDefault()

        if (!isDragging && context.endOfMission) {
            const resolution = getResolution()
            let pointCreator = document.getElementById('point-creator')
            const originalShape = e.target
            setPointName(originalShape.attrs.name)
            console.log(document.getElementsByName('pointType'))

            switch (originalShape.attrs.fill) {
                case 'blue':
                    setPointType([true, false])
                    document.getElementById('pointName').classList.add('is-flex')
                    break;

                case 'red':
                    setPointType([false, true])
                    document.getElementById('pointName').classList.remove('is-flex')
                    break;

                /*case 'orange':
                    setPointType([false, false, true])
                    document.getElementById('pointName').style.display = 'none'
                    break;*/

                default:
                    break;
            }

            context.actions.setShapeRef(originalShape)
            console.log('editing?', context.mapDimensions.resolution, originalShape.attrs.x, originalShape.attrs.y)
            setIsNew(false)
            document.getElementById('point-modal').classList.add('is-active')
            setPreModalValues([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])

            setMenuPosX((originalShape.attrs.x * resolution).toFixed(6))
            setMenuPosY((originalShape.attrs.y * resolution).toFixed(6))
        }
    }

    //POINT EDITOR
    const editMulti = (shapes) => {
        //e.evt.preventDefault()

        if (!isDragging && context.endOfMission && context.selected.tool?._id !== 'show') {
            //setPointName(originalShape.attrs.name)

            console.log('editing?', context.mapDimensions.resolution, shapes)
            setIsNew(false)
            document.getElementById('multi-modal').classList.add('is-active')
            setPreModalValues([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])
        }
    }

    const didMount = (e) => {
        let mounted

        if (e.target.getClassName() === 'Rect') {
            mounted = e.target
            context.actions.setShapeRef(e.target)
        } else {
            mounted = e.target.getStage().findOne('.' + context.selectedRect.name)
        }

        console.log(context.selector, isDragging)

        if (!context.selector) {
            unpopShapes()
            context.actions.setSelector(true)
            console.log(mounted)

            context.actions.setSelectedRect({ ...mounted.attrs })
        } else if (!isDragging) {
            editZone(e)
        }
    }


    //ZONE CREATOR
    const createZone = (e) => {
        const oldScale = e.target.getStage().scaleX()
        const mousePointTo = {
            x: e.target.getStage().getPointerPosition().x / oldScale - e.target.getStage().x() / oldScale,
            y: e.target.getStage().getPointerPosition().y / oldScale - e.target.getStage().y() / oldScale
        }

        if ((e.target._id === clickState || clickState === 0) && !context.pointClickState && !context.selector && !isDragging && context.endOfMission && context.selected.tool?._id !== 'show') {
            document.getElementById('beep').checked = false
            document.getElementById('maxV').value = null
            document.getElementById('zone-modal').classList.add('is-active')
            setPreModalValues([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])

            const newZone = {
                name: 'zone' + repeatCounter,
                fill: '#FFB47F',
                stroke: 'orange',
                strokeWidth: 1,
                opacity: 0.6,
                x: mousePointTo.x - 100 / 2,
                y: mousePointTo.y - 50 / 2,
                height: 50,
                width: 100,
                rotation: 0,
                draggable: 'true',
                scaleX: 1,
                scaleY: 1,
                velocity: 1
            }
            //console.log('new zone!', isDragging)
            context.actions.setSelectedRect({ ...context.selectedRect, name: 'zone' + repeatCounter })
            setIsNew(true)
            setRepeatCounter(repeatCounter + 1)
            context.actions.setKonvaZones([...context.konvaZones, newZone])

            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [context.konvaPoints, context.konvaEdges, [...context.konvaZones, newZone]] }])
            context.actions.setUndone([])
        } else {
            if (e.target.getClassName() !== 'Circle' && e.target.getClassName() !== 'Rect') {
                unpopShapes()
            }
        }
    }


    //ZONE EDITOR
    const editZone = (e) => {
        //e.evt.preventDefault()
        console.log(e.target, context.selectedRect)

        if (!isDragging && context.endOfMission && context.selected.tool?._id !== 'show' && e.target.attrs.name === context.selectedRect.name) {
            console.log('editing!')
            document.getElementById('zone-modal').classList.add('is-active')
            setPreModalValues([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])
            const rectangle = layerRef.current.getStage().findOne('.' + context.selectedRect.name)
            context.actions.setSelectedRect({ ...rectangle.attrs })
            setIsNew(false)

            if (rectangle.attrs.stroke === 'hotpink') {
                document.getElementById('beep').checked = true
            } else document.getElementById('beep').checked = false

            console.log(document.getElementById('maxV').value, rectangle.attrs.velocity)
            document.getElementById('maxV').value = rectangle.attrs.velocity
        }
    }

    //DELETES LAST CREATED SHAPE
    const undoButton = () => {
        if (context.changes.length > 0) {
            context.actions.setKonvaPoints(context.changes[context.changes.length - 1].old[0])
            context.actions.setKonvaEdges(context.changes[context.changes.length - 1].old[1])
            context.actions.setKonvaZones(context.changes[context.changes.length - 1].old[2])

            console.log(context.changes)
            let newChanges = context.changes
            context.actions.setUndone([...context.undone, newChanges.pop()])

            context.actions.setChanges(newChanges)
        }

        document.getElementById('point-modal').classList.remove('is-active')
        document.getElementById('edge-modal').classList.remove('is-active')
        document.getElementById('zone-modal').classList.remove('is-active')

        setIsNew(false)
        console.log('undoing!')
        unpopShapes()
    }

    //DELETES CURRENT POINT
    const deletePoint = () => {
        unpopModal()

        console.log(context.shapeRef.attrs, context.konvaPoints)
        let deletedPoints = context.konvaPoints.map((point) => {
            if (context.shapeRef.attrs.name === point.name) {
                return null
            } else return point
        })

        let filterDeletedP = deletedPoints.filter((point) => {
            return point != null
        })

        let deletedEdges = context.konvaEdges.map((edge) => {
            if ((context.shapeRef.attrs.x === edge.points[0] && context.shapeRef.attrs.y === edge.points[1]) || (context.shapeRef.attrs.x === edge.points[2] && context.shapeRef.attrs.y === edge.points[3])) {
                return null
            } else return edge
        })

        let filterDeletedL = deletedEdges.filter((edge) => {
            return edge != null
        })

        context.actions.setKonvaPoints(filterDeletedP)
        context.actions.setKonvaEdges(filterDeletedL)

        context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [filterDeletedP, filterDeletedL, context.konvaZones] }])
        context.actions.setUndone([])
    }

    //DELETES SELECTED POINTS
    const deletePoints = () => {
        unpopModal()

        console.log(context.selectedShapes, context.konvaPoints)

        let deletedPoints = context.konvaPoints.map((point) => {
            if (context.selectedShapes.some(shape => shape.attrs.name === point.name)) {
                return null
            } else return point
        })

        let filterDeletedP = deletedPoints.filter((point) => {
            return point != null
        })

        let deletedEdges = context.konvaEdges.map((edge) => {
            if ((context.selectedShapes.some(shape => shape.attrs.x === edge.points[0] && shape.attrs.y === edge.points[1])) || (context.selectedShapes.some(shape => shape.attrs.x === edge.points[2] && shape.attrs.y === edge.points[3]))) {
                return null
            } else return edge
        })

        let filterDeletedL = deletedEdges.filter((edge) => {
            return edge != null
        })

        context.actions.setKonvaPoints(filterDeletedP)
        context.actions.setKonvaEdges(filterDeletedL)

        context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [filterDeletedP, filterDeletedL, context.konvaZones] }])
        context.actions.setUndone([])
    }

    const deleteZone = () => {
        unpopModal()

        let deletedZones = context.konvaZones.map((zone) => {
            if (context.selectedRect.name === zone.name) {
                return null
            } else return zone
        })

        let filterDeleted = deletedZones.filter((zone) => {
            return zone != null
        })

        context.actions.setKonvaZones(filterDeleted)

        context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [context.konvaPoints, context.konvaEdges, filterDeleted] }])
        context.actions.setUndone([])
    }

    const deleteEdge = () => {
        unpopModal()

        let deletedEdges = context.konvaEdges.map((edge) => {
            if (context.shapeRef.attrs.name === edge.name) {
                return null
            } else return edge
        })

        let filterDeleted = deletedEdges.filter((edge) => {
            return edge != null
        })

        context.actions.setKonvaEdges(filterDeleted)

        context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [context.konvaPoints, filterDeleted, context.konvaZones] }])
        context.actions.setUndone([])
    }

    //CHECKS IF A NEW POINT IS BEING CREATED OR IF ONE IS BEING CURRENTLY EDITED FOR DELETING PURPOSES
    const newCheck = () => {
        //console.log(isNew)
        if (isNew) {
            undoButton()
        } else {
            unpopModal()
        }
    }

    //NAME EDITOR FOR POINTS THAT CHECKS IF THE NAME ALREADY EXISTS
    const nameChecker = (name) => {
        let savedNames

        if (pointName !== '') {
            savedNames = new Set(context.konvaPoints.map(point => point.name))
        } else {
            savedNames = new Set(context.konvaZones.map(zone => zone.name))
        }

        console.log(savedNames, name)

        let newName = name
        let counter = 1

        while (savedNames.has(newName)) {
            newName = name + counter
            counter++
        }

        return newName
    }

    const renamePoint = (oldName) => {
        if (oldName !== context.shapeRef.attrs.name) {
            const actualName = nameChecker(oldName)

            console.log(context.shapeRef.attrs.name, oldName, actualName)

            const updatedPoints = context.konvaPoints.map(point => {
                if (point.name === context.shapeRef.attrs.name) {
                    return { ...point, name: actualName }
                } else return point
            })

            const updatedEdges = context.konvaEdges.map(edge => {
                const [point1, point2] = edge.pointIds

                if (point1 === context.shapeRef.attrs.name || point2 === context.shapeRef.attrs.name) {
                    const newPoint1 = point1 === context.shapeRef.attrs.name ? actualName : point1
                    const newPoint2 = point2 === context.shapeRef.attrs.name ? actualName : point2

                    return {
                        ...edge,
                        pointIds: [newPoint1, newPoint2],
                        name: newPoint1 + ' a ' + newPoint2
                    }
                }

                return edge
            })

            return {
                newPoints: updatedPoints,
                newEdges: updatedEdges,
                actualName
            }
        } else {
            return {
                newPoints: context.konvaPoints,
                newEdges: context.konvaEdges,
                actualName: oldName
            }
        }
    }

    const nameSaver = (e) => {
        setPointName(e.target.value)
    }

    const handleCommit = () => {
        if (pointName !== '') {
            const oldName = pointName

            const { newPoints, newEdges, actualName } = renamePoint(oldName)

            context.actions.setKonvaPoints(newPoints)
            context.actions.setKonvaEdges(newEdges)

            setPointName(actualName)
        } else {
            const oldName = context.selectedRect.name

            const { newZones, actualName } = renameZone(oldName)

            context.actions.setKonvaZones(newZones)

            context.actions.setSelectedRect({ ...context.selectedRect, name: actualName })
        }

    }

    //NAME EDITOR FOR ZONES THAT CHECKS IF THE NAME ALREADY EXISTS
    const renameZone = (oldName) => {
        console.log(oldName, context.shapeRef)
        if (oldName !== context.shapeRef.attrs.name) {
            const actualName = nameChecker(oldName)

            const updatedZones = context.konvaZones.map(zone => {
                if (zone.name === context.shapeRef.attrs.name) {
                    return { ...zone, name: actualName }
                } else return zone
            })

            console.log(oldName, actualName, updatedZones)

            return {
                newZones: updatedZones,
                actualName
            }
        } else {
            return {
                newZones: context.konvaZones,
                actualName: oldName
            }
        }
    }

    const zoneNameSaver = (e) => {
        context.actions.setSelectedRect({ ...context.selectedRect, name: e.target.value })
    }

    const updateZones = () => {
        let counter = 0, newZones = [...context.konvaZones]
        layerRef.current.children.forEach((child) => {
            if (child.getClassName() === 'Rect' && child.attrs.name) {
                newZones[counter].x = child.attrs.x
                newZones[counter].y = child.attrs.y
                newZones[counter].scaleX = child.attrs.scaleX
                newZones[counter].scaleY = child.attrs.scaleY
                newZones[counter].rotation = child.attrs.rotation
                counter += 1
            }
        })

        context.actions.setKonvaZones([...newZones])

        let oldZones = context.konvaZones.map((a) => {
            if (a.name === context.selectedRect.name) {
                context.actions.setSelectedRect({ ...a })

                return {
                    ...a,
                    x: context.selectedRect.x,
                    y: context.selectedRect.y,
                    scaleX: context.selectedRect.scaleX,
                    scaleY: context.selectedRect.scaleY,
                    rotation: context.selectedRect.rotation
                }
            } else return a
        })

        context.changes.push({ old: [deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(oldZones)], new: [deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(newZones)] })

        context.actions.setChanges([...context.changes])
        context.actions.setUndone([])
    }

    const velocityChange = (e) => {
        const zoneVelocity = context.konvaZones.map((a) => {
            if (!isNew) {
                if (a.name === context.selectedRect.name) {
                    return {
                        ...a,
                        velocity: parseFloat(document.getElementById('maxV').value),
                    }
                } else return a
            } else {
                if (a.name === context.shapeRef.attrs.name) {
                    return {
                        ...a,
                        velocity: parseFloat(document.getElementById('maxV').value),
                    }
                } else return a
            }
        })

        console.log(zoneVelocity, document.getElementById('maxV').value)
        context.actions.setKonvaZones(zoneVelocity)
    }

    //MANUAL COORDINATES FOR POINTS HANDLER
    const manualCoords = (e) => {
        const resolution = getResolution()
        var xValue = Number(document.getElementById('xPos').value) / resolution
        var yValue = Number(document.getElementById('yPos').value) / resolution

        console.log('changed coords', xValue, yValue, context.mapDimensions.resolution)

        console.log(imageRef.current.getWidth(), e.target.value)

        let displayValueX, displayValueY = ''

        console.log(e.target.value.search(/[.]/))

        //if (imageHitbox.intersects({ x: pos.x, y: pos.y })) {
        if (xValue <= imageRef.current.getWidth() && yValue <= imageRef.current.getHeight() && xValue >= 0 && yValue >= 0) {
            /*if (e.target.value.charAt(e.target.value.length - 1) === '.' && e.target.id === 'xPos') {
                displayValueX = xValue * context.mapDimensions.resolution + '.'
                displayValueY = yValue * context.mapDimensions.resolution
            } else if (e.target.value.charAt(e.target.value.length - 1) === '.' && e.target.id === 'yPos') {
                displayValueX = xValue * context.mapDimensions.resolution
                displayValueY = yValue * context.mapDimensions.resolution + '.'
            } else if (e.target.value.charAt(e.target.value.length - 1) === '0' && e.target.value.search(/[.]/) !== -1 && e.target.id === 'xPos') {
                if (e.target.value.charAt(e.target.value.length - 2) === '.' || (e.target.value.charAt(e.target.value.length - 2) === '0' && e.target.value.charAt(e.target.value.length - 3) === '.')) {
                    displayValueX = xValue * context.mapDimensions.resolution + '.0'
                    displayValueY = yValue * context.mapDimensions.resolution
                } else {
                    displayValueX = xValue * context.mapDimensions.resolution + '0'
                    displayValueY = yValue * context.mapDimensions.resolution
                }
            } else if (e.target.value.charAt(e.target.value.length - 1) === '0' && e.target.value.search(/[.]/) !== -1 && e.target.id === 'yPos') {
                if (e.target.value.charAt(e.target.value.length - 2) === '.' || (e.target.value.charAt(e.target.value.length - 2) === '0' && e.target.value.charAt(e.target.value.length - 3) === '.')) {
                    displayValueX = xValue * context.mapDimensions.resolution
                    displayValueY = yValue * context.mapDimensions.resolution + '.0'
                } else {
                    displayValueX = xValue * context.mapDimensions.resolution
                    displayValueY = yValue * context.mapDimensions.resolution + '0'
                }
            } else {
                if (e.target.id === 'xPos') {
                    displayValueX = Number(e.target.value)
                    displayValueY = Number(document.getElementById('yPos').value)
                } else {
                    displayValueX = Number(document.getElementById('xPos').value)
                    displayValueY = Number(e.target.value)
                }
                
            }*/

            if (e.target.id === 'xPos') {
                displayValueX = e.target.value
                displayValueY = document.getElementById('yPos').value
            } else {
                displayValueX = document.getElementById('xPos').value
                displayValueY = e.target.value
            }

            console.log(displayValueX, displayValueY)
            setMenuPosX(displayValueX)
            setMenuPosY(displayValueY)

            console.log('in bounds', context.shapeRef, isNew)
            let newCoords

            if (!isNew) {
                newCoords = context.konvaPoints.map((p, i) => {
                    if (context.shapeRef.attrs.name === p.name) {
                        return {
                            ...p,
                            x: xValue,
                            y: yValue
                        }
                    } else return p
                })
            } else {
                newCoords = context.konvaPoints.map((p, i) => {
                    if (i === context.konvaPoints.length - 1) {
                        return {
                            ...p,
                            x: xValue,
                            y: yValue
                        }
                    } else return p
                })
            }

            context.actions.setKonvaPoints(newCoords)
        }
    }

    //MANUAL COORDINATES FOR POINTS HANDLER
    const manualOffset = () => {
        var xValue = Number(document.getElementById('xOffset').value) / context.mapDimensions.resolution
        var yValue = Number(document.getElementById('yOffset').value) / context.mapDimensions.resolution

        console.log('changed coords', xValue, yValue, context.mapDimensions.resolution)

        let newCoords = context.konvaPoints

        context.selectedShapes.forEach(shape => {
            newCoords = newCoords.map((p, i) => {
                if (p.name === shape.attrs.name) {
                    return {
                        ...p,
                        x: p.x + xValue,
                        y: p.y + yValue
                    }
                } else return p
            })
        })

        document.getElementById('xOffset').value = ''
        document.getElementById('yOffset').value = ''

        context.actions.setKonvaPoints(newCoords)
    }

    //POINT TYPE HANDLER
    const pointProhibition = (e) => {
        let pointCreator = document.getElementById('point-creator')
        let redTurner, blueTurner, greenTurner = null
        console.log(context.shapeRef, e)
        let toBeChanged

        if (!isNew) {
            toBeChanged = context.shapeRef
        } else {
            toBeChanged = layerRef.current.children[layerRef.current.children.length - 2]
        }

        switch (e.target.value) {
            case 'nav':
                blueTurner = context.konvaPoints.map((p, i) => {
                    if (toBeChanged.attrs.name === p.name) {
                        return {
                            ...p,
                            fill: 'blue',
                            radius: 5
                        }
                    } else return p
                })

                context.actions.setKonvaPoints(blueTurner)
                setPointType([true, false, false])
                document.getElementById('pointName').classList.add('is-flex')
                break;

            case 'nogo':
                redTurner = context.konvaPoints.map((p, i) => {
                    if (toBeChanged.attrs.name === p.name) {
                        return {
                            ...p,
                            fill: 'red',
                            radius: 4
                        }
                    } else return p
                })

                context.actions.setKonvaPoints(redTurner)
                setPointType([false, true, false])
                document.getElementById('pointName').classList.remove('is-flex')
                break;

            /*case 'slow':
                greenTurner = context.konvaPoints.map((p, i) => {
                    if (toBeChanged.attrs.name === p.name) {
                        return {
                            ...p,
                            fill: 'orange',
                            radius: 4
                        }
                    } else return p
                })

                context.actions.setKonvaPoints(greenTurner)
                setPointType([false, false, true])
                document.getElementById('pointName').classList.remove('is-flex')
                break;*/

            default:
                break;
        }
    }

    //EDGE AVOIDANCE HANDLER
    const onChangeAvoid = () => {
        let edgeEditor = document.getElementById('edge-editor')

        //console.log(edgeEditor.objAv.checked)

        const edgeAvoid = context.konvaEdges.map((l) => {
            if (l.name === context.shapeRef.attrs.name) {
                if (edgeEditor.objAv.checked) {
                    return {
                        ...l,
                        stroke: 'blue',
                        fill: 'blue'
                    }
                } else return {
                    ...l,
                    stroke: 'purple',
                    fill: 'purple'
                }
            } else return l
        })

        context.actions.setKonvaEdges(edgeAvoid)
    }

    //ZONE BEEP HANDLER
    const onChangeBeep = () => {
        let zoneCreator = document.getElementById('zone-creator')
        console.log(context.shapeRef)

        const zoneBeep = context.konvaZones.map((a) => {
            if (!isNew) {
                if (a.name === context.selectedRect.name) {
                    if (!document.getElementById('beep').checked) {
                        return {
                            ...a,
                            stroke: 'orange',
                            fill: '#FFB47F'
                        }
                    } else return {
                        ...a,
                        stroke: 'hotpink',
                        fill: '#FF7FE8'
                    }
                } else return a
            } else {
                console.log(a, context.shapeRef)
                if (a.name === context.shapeRef.attrs.name) {
                    if (!document.getElementById('beep').checked) {
                        return {
                            ...a,
                            stroke: 'orange',
                            fill: '#FFB47F'
                        }
                    } else return {
                        ...a,
                        stroke: 'hotpink',
                        fill: '#FF7FE8'
                    }
                } else return a
            }
        })

        context.actions.setKonvaZones(zoneBeep)
    }

    //EDGE DIRECTION HANDLER
    const edgeUpdate = (e) => {
        let edgeEditor = document.getElementById('edge-editor')
        //console.log(edgeEditor.direction.value)

        const edgeDir = context.konvaEdges.map((l) => {
            if (l.name === context.shapeRef.attrs.name) {
                switch (e.target.value) {
                    case 'two':
                        setEdgeType([true, false, false])

                        return {
                            ...l,
                            pointerAtBeginning: false,
                            pointerAtEnding: false
                        }

                    case 'str':
                        setEdgeType([false, true, false])

                        return {
                            ...l,
                            pointerAtBeginning: false,
                            pointerAtEnding: true
                        }

                    case 'inv':
                        setEdgeType([false, false, true])

                        return {
                            ...l,
                            pointerAtBeginning: true,
                            pointerAtEnding: false
                        }

                    default:
                        return l
                }
            } else return l
        })

        context.actions.setKonvaEdges(edgeDir)
    }

    //EDGE EDITING MENU
    const edgeMenu = (e) => {
        if (context.endOfMission && context.selected.tool?._id !== 'show') {
            let edgeEditor = document.getElementById('edge-modal')

            //console.log(e.target.attrs.fill, edgeEditor.style)

            //shapeEditor.style.top = 85.875 + e.target.getStage().getPointerPosition().y + 4 + 'px'
            //shapeEditor.style.left = e.target.getStage().getPointerPosition().x + 4 + 'px'

            if ((e.target.attrs.stroke === 'blue' || e.target.attrs.stroke === 'purple') && !isDragging) {
                //console.log('WHERE IS THE POP UP')
                if (e.target.attrs.pointerAtEnding) {
                    setEdgeType([false, true, false])
                } else if (e.target.attrs.pointerAtBeginning) {
                    //console.log('reverse!')
                    setEdgeType([false, false, true])
                } else setEdgeType([true, false, false])

                if (e.target.attrs.fill === 'purple') {
                    document.getElementById('objAv').checked = true
                } else document.getElementById('objAv').checked = false
                edgeEditor.classList.add('is-active')
                setPreModalValues([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])
                //console.log(edgeEditor)
                context.actions.setShapeRef(e.target)
            }
        }
    }

    const pointClick = (e) => {
        console.log(context.pointClickState, context.shapeRef, e.target)
        if (context.selected.tool?._id !== 'show') {
            if (!context.pointClickState) {
                popShape(e)
                if (context.endOfMission) edgeHandler(e)
                context.actions.setSelector(false)
            } else {
                if (context.shapeRef === e.target && context.endOfMission && context.selectedShapes.length < 2) {
                    editPoint(e)
                } else {
                    if (context.selectedShapes.length > 1 && context.selectedShapes.some(shape => shape._id === context.shapeRef._id)) {
                        editMulti(context.selectedShapes)
                    }
                    unpopShapes()
                    popShape(e)
                    if (context.endOfMission) {
                        edgeHandler(e)
                    }
                }
            }
        }
    }

    const zoneClick = (e) => {
        console.log(context.selector, context.shapeRef, e.target)
        if (context.endOfMission) {
            if (!context.selector) {
                didMount(e)
            } else {
                if (context.shapeRef === e.target) {
                    editZone(e)
                } else {
                    didMount(e)
                }
            }
        }
    }

    //INCREASES POINT SIZE AND ADDS LABEL WITH NAME ON CLICK
    const popShape = (e) => {
        if (context.selectedShapes.length === 0) context.actions.setShapeRef(e.target)
        context.actions.setPointClickState(true)
        setPointName(e.target.attrs.name)
        if (e.target.getClassName() === 'Circle') {
            e.target.scaleX(1.5)
            e.target.scaleY(1.5)
        }
        //console.log(e.target, isDragging)
        if (e.target.getClassName() === 'Arrow') {
            setPointLabel({ x: (e.target.attrs.points[0] + e.target.attrs.points[2]) / 2, y: (e.target.attrs.points[1] + e.target.attrs.points[3]) / 2, name: '' })
        } else {
            setPointLabel({ x: e.target.attrs.x, y: e.target.attrs.y, name: e.target.attrs.name })
        }

        console.log(e.target)
        if (e.target.attrs.fill === 'blue') setLabelVisibility(true)
    }

    //DECREASES POINT SIZE AND REMOVES LABEL WITH NAME ON CLICK OFF
    const unpopShapes = () => {
        if (context.selected.tool?._id !== 'show') {
            setLabelVisibility(false)
            document.body.style.cursor = 'default'
            context.actions.setPointClickState(false)
            setPointName('')
            setEdgeState(0)
            if (!context.mappingStatus) {
                for (let i = 0; i < layerRef.current.children.length; i++) {
                    if (layerRef.current.children[i].getClassName() === 'Circle') {
                        layerRef.current.children[i].scaleX(1)
                        layerRef.current.children[i].scaleY(1)
                    }
                }
            }

            context.actions.setSelector(false)
            context.actions.setSelectedRect()
        }
    }

    //MODAL DISAPPEARING HANDLER
    const unpopModal = () => {
        unpopShapes()
        setPointName('')
        context.actions.setSelectedRect()
        context.actions.setSelectedShapes([])
        trRef.current.nodes([])
        setEdgesInfo([])
        setIsNew(false)
        //shapeRef.current = {}
        if (JSON.stringify(preModalValues) !== JSON.stringify([deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)])) {
            context.actions.setChanges([...context.changes, { old: preModalValues, new: [deepClone(context.konvaPoints), deepClone(context.konvaEdges), deepClone(context.konvaZones)] }])
            context.actions.setUndone([])
        }

        document.getElementById('point-modal').classList.remove('is-active')
        document.getElementById('multi-modal').classList.remove('is-active')
        document.getElementById('edge-modal').classList.remove('is-active')
        document.getElementById('zone-modal').classList.remove('is-active')
    }

    //PREVENTS REFRESH THROUGH PRESSING "ENTER" IN THE FORMS
    const preventRefresh = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            return false
        }
    }

    //HOTKEY HANDLER
    const hotkeyHandler = (e) => {
        if (!document.getElementById('point-modal').classList.contains("is-active")
            && !document.getElementById('multi-modal').classList.contains("is-active")
            && !document.getElementById('zone-modal').classList.contains("is-active")
            && !document.getElementById('edge-modal').classList.contains("is-active")
        ) {
            if (e.ctrlKey) {
                switch (e.key) {
                    case 'c':
                        //copy
                        let pointsToCopy, pointNames = [], edgesToCopy = []
                        if (context.selectedShapes.length > 0) {
                            pointsToCopy = context.selectedShapes.map(shape => {
                                pointNames.push(shape.attrs.name)

                                let newName = nameChecker(shape.attrs.name)

                                return {
                                    ...shape.attrs,
                                    name: newName,
                                    scaleX: 1,
                                    scaleY: 1,
                                    x: shape.attrs.x + 15,
                                    y: shape.attrs.y - 15
                                }
                            })

                            context.konvaEdges.forEach(edge => {
                                if (pointNames.some(point => point === edge.pointIds[0]) && pointNames.some(point => point === edge.pointIds[1])) {
                                    let firstIndex = context.selectedShapes.findIndex(shape => shape.attrs.name === edge.pointIds[0])
                                    let lastIndex = context.selectedShapes.findIndex(shape => shape.attrs.name === edge.pointIds[1])

                                    edgesToCopy.push({
                                        ...edge,
                                        name: pointsToCopy[firstIndex].name + ' a ' + pointsToCopy[lastIndex].name,
                                        pointIds: [pointsToCopy[firstIndex].name, pointsToCopy[lastIndex].name],
                                        points: [edge.points[0] + 5, edge.points[1] - 5, edge.points[2] + 5, edge.points[3] - 5]
                                    })
                                }
                            })

                            context.actions.setPointCopy(pointsToCopy)
                            context.actions.setEdgeCopy(edgesToCopy)

                            context.actions.setMessage({ message: "Copied points to clipboard!", type: "success" })
                        } else if (context.pointClickState && context.shapeRef) {
                            console.log(context.shapeRef)

                            let newName = nameChecker(context.shapeRef.attrs.name)

                            context.actions.setPointCopy([{
                                ...context.shapeRef.attrs,
                                name: newName,
                                scaleX: 1,
                                scaleY: 1,
                                x: context.shapeRef.attrs.x + 15,
                                y: context.shapeRef.attrs.y - 15
                            }])


                            context.actions.setMessage({ message: "Copied point to clipboard!", type: "success" })
                        } else if (context.selectedRect) {
                            console.log(context.selectedRect)

                            let newName = nameChecker(context.selectedRect.name)

                            context.actions.setZoneCopy([{
                                ...context.selectedRect,
                                name: newName,
                                scaleX: 1,
                                scaleY: 1,
                                x: context.shapeRef.attrs.x + 20,
                                y: context.shapeRef.attrs.y - 20
                            }])

                            context.actions.setMessage({ message: "Copied zone to clipboard!", type: "success" })
                        }

                        console.log('COPYING: ', pointsToCopy, edgesToCopy)
                        break

                    case 'v':
                        //paste
                        let pastedPoints, pastedEdges
                        if (context.pointCopy.length > 0) {
                            pastedPoints = context.konvaPoints.concat(context.pointCopy)
                            context.actions.setKonvaPoints(pastedPoints)

                            if (context.edgeCopy.length > 0) {
                                pastedEdges = context.konvaEdges.concat(context.edgeCopy)
                                context.actions.setKonvaEdges(pastedEdges)
                            } else {
                                pastedEdges = context.konvaEdges
                            }

                            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [pastedPoints, pastedEdges, context.konvaZones] }])
                            context.actions.setUndone([])
                            context.actions.setPointCopy([])
                            context.actions.setPasted(context.pointCopy)

                            console.log('PASTING: ', pastedPoints, pastedEdges)
                        } else if (context.zoneCopy.length > 0) {
                            const pastedZones = context.konvaZones.concat(context.zoneCopy)
                            context.actions.setKonvaZones(pastedZones)

                            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [context.konvaPoints, context.konvaEdges, pastedZones] }])
                            context.actions.setUndone([])
                            context.actions.setZoneCopy([])

                            unpopShapes()
                        }
                        break

                    case 'z':
                        //undo
                        undoButton()
                        break

                    case 'y':
                        //redo
                        if (context.undone.length > 0) {
                            context.actions.setKonvaPoints([...context.undone[context.undone.length - 1].new[0]])
                            context.actions.setKonvaEdges([...context.undone[context.undone.length - 1].new[1]])
                            context.actions.setKonvaZones([...context.undone[context.undone.length - 1].new[2]])

                            context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [context.undone[context.undone.length - 1].new[0], context.undone[context.undone.length - 1].new[1], context.undone[context.undone.length - 1].new[2]] }])

                            console.log(context.undone)
                            let newUndone = [...context.undone]
                            newUndone.pop()

                            context.actions.setUndone(newUndone)

                            context.actions.setSelector(false)
                        }
                        break

                    case 'a':
                        //select all
                        const circles = layerRef.current.getChildren().filter((node) => node.getClassName() === 'Circle')

                        circles.forEach((circle) => popShape({ target: circle }))

                        context.actions.setSelectedShapes(circles)
                        trRef.current.nodes(circles)
                        setLabelVisibility(false)

                        break

                    default:
                        break
                }
            } else if (e.key === 'Delete') {
                if (context.selectedShapes.length > 0) {
                    unpopModal()

                    let deletedPoints = context.konvaPoints
                    let deletedEdges = context.konvaEdges
                    let filteredPoints, filteredEdges
                    context.selectedShapes.forEach(shape => {
                        deletedPoints = deletedPoints.map((point) => {
                            if (point && shape.attrs.name === point.name) {
                                return null
                            } else return point
                        })


                        deletedEdges = deletedEdges.map((edge) => {
                            if (edge && ((shape.attrs.x === edge.points[0] && shape.attrs.y === edge.points[1]) || (shape.attrs.x === edge.points[2] && shape.attrs.y === edge.points[3]))) {
                                return null
                            } else return edge
                        })
                    })

                    filteredPoints = deletedPoints.filter((point) => {
                        return point != null
                    })


                    filteredEdges = deletedEdges.filter((edge) => {
                        return edge != null
                    })
                    context.actions.setKonvaPoints(filteredPoints)
                    context.actions.setKonvaEdges(filteredEdges)

                    context.actions.setChanges([...context.changes, { old: [context.konvaPoints, context.konvaEdges, context.konvaZones], new: [filteredPoints, filteredEdges, context.konvaZones] }])
                    context.actions.setUndone([])
                } else if (context.pointClickState && context.shapeRef) {
                    deletePoint()
                }
            }
        }
    }

    //SELECT BOX FUNCTIONS
    const handleMouseDown = (e) => {
        const stage = stageRef.current;
        const layer = layerRef.current;
        const pos = stage.getRelativePointerPosition(layer)

        if (e.target.getClassName() !== 'Rect' && context.selectedShapes.length === 0) unpopShapes() //unpopShapes was preventing zones from being transformed as selector disappeared with Select tool

        if (e.evt.button === 2 || e.evt.touches?.length === 2) {
            setDraggable(true)
            stage.startDrag()
            document.body.style.cursor = 'grabbing'
        } else {
            setDraggable(false)
            stage.draggable(false)
            if (e.target.getClassName() !== 'Circle' && e.target.getClassName() !== 'Rect' && e.target.getClassName() !== 'Arrow') {
                selecting.current = true
                setSelectionBox({ visible: true, x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y, x: pos.x, y: pos.y, width: 0, height: 0 })
            }
        }
    }

    const handleMouseMove = (e) => {
        if (!selecting.current) return
        else {
            const stage = stageRef.current;
            const layer = layerRef.current;
            const pos = stage.getRelativePointerPosition(layer)

            setSelectionBox((prev) => {
                const x1 = prev.x1;
                const y1 = prev.y1;
                const x2 = pos.x;
                const y2 = pos.y;

                return {
                    visible: true,
                    x1,
                    y1,
                    x2,
                    y2,
                    x: Math.min(x1, x2),
                    y: Math.min(y1, y2),
                    width: Math.abs(x2 - x1),
                    height: Math.abs(y2 - y1),
                };
            })
        }
    }

    const handleMouseUp = (e) => {
        const stage = stageRef.current

        if (selectionRectRef.current) {
            if (e.evt.button === 2) {
                setDraggable(false)
                stage.stopDrag()
                document.body.style.cursor = 'default'
            } else if (!isDragging) {
                selecting.current = false
                setSelectionBox((prev) => ({ ...prev, visible: false }))

                const layer = layerRef.current
                if (!stage || !layer) return

                const box = selectionRectRef.current.getClientRect()

                const circles = layer.getChildren().filter((node) => node.getClassName() === 'Circle' && Konva.Util.haveIntersection(box, node.getClientRect()))

                circles.forEach((circle) => popShape({ target: circle }))

                console.log('SELECTED SHAPES: ', circles)
                context.actions.setSelectedShapes(circles)
                trRef.current.nodes(circles)
                if (circles.length > 1) setLabelVisibility(false)
                else {
                    if (e.target.getClassName() !== 'Circle') unpopShapes()
                }
            }
        }
    }

    const handleContextMenu = (e) => {
        e.evt.preventDefault()
    }

    //RENDER
    return (
        <div tabIndex={0} onKeyUp={context.endOfMission && context.selected.tool._id !== 'show' && hotkeyHandler}>
            <div className='modal' id='point-modal'>
                <div className="modal-background" onClick={newCheck}></div>
                <div className='modal-card'>
                    <header className='modal-card-head'>
                        <p className='modal-card-title'>Point</p>
                        <button className="delete" aria-label="close" onClick={newCheck}></button>
                    </header>
                    <section className='modal-card-body' id='point-creator'>
                        <div className='controls'>
                            <div className='columns is-multiline is-align-items-center'>
                                <div className='column is-two-fifths is-flex is-align-items-center' style={{ padding: 0 }}>
                                    <label htmlFor='xPos' style={{ margin: 'auto', textAlign: 'left', width: '0.75rem' }}>x:</label>
                                    <input className='input' style={{ width: "10rem", marginRight: "0.5rem" }} type='text' id='xPos' value={menuPosX} onChange={manualCoords} />
                                    <label htmlFor='xPos' style={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 'auto', textAlign: 'left', width: '0.75rem' }}>m</label>
                                </div>

                                <div className='column is-two-fifths is-flex is-align-items-center' style={{ padding: 0 }}>
                                    <label htmlFor='yPos' style={{ margin: 'auto', textAlign: 'left', width: '0.75rem' }}>y:</label>
                                    <input className='input' style={{ width: "10rem", marginRight: "0.5rem" }} type='text' id='yPos' value={menuPosY} onChange={manualCoords} />
                                    <label htmlFor='yPos' style={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 'auto', textAlign: 'left', width: '0.75rem' }}>m</label>
                                </div>

                                <div className='column is-two-fifths is-flex is-align-items-center' style={{ justifyContent: 'center', fontSize: '0.8rem', padding: 0 }}>
                                    (max = {(context.mapDimensions.width * context.mapDimensions.resolution).toFixed(2)}m)
                                </div>

                                <div className='column is-two-fifths is-flex is-align-items-center' style={{ justifyContent: 'center', fontSize: '0.8rem', padding: 0 }}>
                                    (max = {(context.mapDimensions.height * context.mapDimensions.resolution).toFixed(2)}m)
                                </div>
                            </div>

                            <div style={{ color: 'darkblue' }}><input type='radio' name='pointType' id='nav' value='nav' checked={pointType[0]} onChange={pointProhibition} />&nbsp;Navigation</div>
                            <div style={{ color: 'darkred' }}><input type='radio' name='pointType' id='nogo' value='nogo' checked={pointType[1]} onChange={pointProhibition} />&nbsp;Prohibition</div>

                            {/*<div style={{ color: 'darkorange' }}><input type='radio' name='pointType' id='slow' value='slow' checked={pointType[2]} onChange={pointProhibition} />&nbsp;Slow Zone</div>*/}

                            <br />

                            <div className='control is-flex' style={{ width: '40%', display: 'none' }} id='pointName'>
                                <label style={{ marginTop: 'auto', marginBottom: 'auto', textAlign: 'left' }}>Name:&nbsp;</label>
                                <input className='input' placeholder="Type an ID" value={pointName} type='text' name='pointName' style={{ width: "10rem", marginRight: "0.5rem" }} onKeyDown={preventRefresh} onChange={nameSaver} onBlur={handleCommit} autoFocus />
                            </div>
                        </div>
                    </section>
                    <footer className='modal-card-foot'>
                        <button className='button is-success' onClick={unpopModal} style={{ marginRight: '10px' }}>
                            <span className="icon is-small">
                                <FontAwesomeIcon icon={faSave} className="is-small" />
                            </span>
                            <span>
                                Save
                            </span>
                        </button>
                        {isNew &&
                            <button className='button' onClick={undoButton}>Cancel</button>
                        }
                        {!isNew &&
                            <button className='button is-danger' onClick={deletePoint} style={{ marginRight: '0.5rem' }}>Delete Point</button>
                        }
                    </footer>
                </div>
            </div>

            <div className='modal' id='multi-modal'>
                <div className="modal-background" onClick={unpopModal}></div>
                <div className='modal-card'>
                    <header className='modal-card-head'>
                        <p className='modal-card-title'>Point Group</p>
                        <button className="delete" aria-label="close" onClick={unpopModal}></button>
                    </header>
                    <section className='modal-card-body' id='point-creator'>
                        <div className='controls'>
                            <div className='columns is-multiline is-align-items-center mb-4'>
                                <div className="column is-full mb-2">
                                    Selected points:
                                </div>

                                <div style={{ maxHeight: '200px', width: '100%', overflow: 'auto' }}>
                                    {context.selectedShapes.map(shape => {
                                        return (<div className="column is-full mb-1">
                                            <FontAwesomeIcon icon={faCircle} className="is-small mr-2" style={{ color: shape.attrs.fill }} />
                                            {shape.attrs.name}
                                        </div>)
                                    })}
                                </div>

                                <div className="column is-full mb-2 mt-2">
                                    Offset:
                                </div>

                                <div className='column is-two-fifths is-flex is-align-items-center' style={{ padding: 0 }}>
                                    <label htmlFor='xOffset' style={{ margin: 'auto', textAlign: 'left', width: '0.75rem' }}>x:</label>
                                    <input className='input' style={{ width: "10rem", marginRight: "0.5rem" }} type='text' id='xOffset' placeholder='0' />
                                    <label htmlFor='xOffset' style={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 'auto', textAlign: 'left', width: '0.75rem' }}>m</label>
                                </div>

                                <div className='column is-two-fifths is-flex is-align-items-center' style={{ padding: 0 }}>
                                    <label htmlFor='yOffset' style={{ margin: 'auto', textAlign: 'left', width: '0.75rem' }}>y:</label>
                                    <input className='input' style={{ width: "10rem", marginRight: "0.5rem" }} type='text' id='yOffset' placeholder='0' />
                                    <label htmlFor='yOffset' style={{ marginTop: 'auto', marginBottom: 'auto', marginRight: 'auto', textAlign: 'left', width: '0.75rem' }}>m</label>
                                </div>
                            </div>
                        </div>
                    </section>
                    <footer className='modal-card-foot'>
                        <button className='button is-success' onClick={() => { unpopModal(); manualOffset() }} style={{ marginRight: '10px' }}>
                            <span className="icon is-small">
                                <FontAwesomeIcon icon={faSave} className="is-small" />
                            </span>

                            <span>
                                Save
                            </span>
                        </button>

                        <button className='button is-danger' onClick={deletePoints} style={{ marginRight: '0.5rem' }}>Delete Points</button>
                    </footer>
                </div>
            </div>

            <div className='modal' id='zone-modal'>
                <div className="modal-background" onClick={newCheck}></div>
                <div className='modal-card'>
                    <header className='modal-card-head'>
                        <p className='modal-card-title'>Zone</p>
                        <button className="delete" aria-label="close" onClick={newCheck}></button>
                    </header>
                    <section className='modal-card-body' id='zone-creator'>
                        <div className='controls'>
                            <div className='control is-flex' style={{ width: '60%', marginBottom: '1rem' }} id='zoneVelocity'>
                                <label style={{ marginTop: 'auto', marginBottom: 'auto', textAlign: 'left' }}>Max. Velocity:&nbsp;</label>
                                <input className='input' style={{ width: "10rem", marginRight: "0.5rem" }} placeholder='1.0' min='0' step='0.1' type='number' id='maxV' onChange={velocityChange} />
                                <label style={{ marginTop: 'auto', marginBottom: 'auto', textAlign: 'left' }}>&nbsp;m/s</label>
                            </div>

                            <div className='control is-flex' style={{ width: '60%', marginBottom: '1rem' }} id='zoneBeep'>
                                <div id='beep' style={{ marginTop: '10px' }}>Beep:&nbsp;<input type='checkbox' id='beep' onChange={onChangeBeep}></input></div>
                            </div>

                            <div className='control is-flex' style={{ width: '40%', marginTop: '1.9rem' }} id='zoneName'>
                                <label style={{ marginTop: 'auto', marginBottom: 'auto', textAlign: 'left' }}>Name:&nbsp;</label>
                                <input className='input' placeholder="Type an ID" value={context.selectedRect?.name} type='text' name='zoneName' style={{ width: "10rem", marginRight: "0.5rem" }} onKeyDown={preventRefresh} onChange={zoneNameSaver} onBlur={handleCommit} autoFocus />
                            </div>
                        </div>
                    </section>
                    <footer className='modal-card-foot'>
                        <button className='button is-success' onClick={unpopModal} style={{ marginRight: '10px' }}>
                            <span className="icon is-small">
                                <FontAwesomeIcon icon={faSave} className="is-small" />
                            </span>
                            <span>
                                Save
                            </span>
                        </button>
                        {isNew &&
                            <button className='button' onClick={undoButton}>Cancel</button>
                        }
                        {!isNew &&
                            <button className='button is-danger' onClick={deleteZone} style={{ marginRight: '0.5rem' }}>Delete Zone</button>
                        }
                    </footer>
                </div>
            </div>

            <div className='modal' id='edge-modal'>
                <div className='modal-background' onClick={unpopModal}></div>
                <div className='modal-card'>
                    <header className='modal-card-head'>
                        <p className='modal-card-title'>Edge</p>
                        <button className='delete' aria-label='close' onClick={unpopModal}></button>
                    </header>
                    <form className='modal-card-body' id='edge-editor'>
                        {context.shapeRef &&
                            <div>{context.shapeRef.attrs?.name?.toString()}</div>
                        }
                        <div style={{ marginTop: '10px' }}>2-way&nbsp;<input type='radio' name='direction' id='bothways' value='two' checked={edgeType[0]} onChange={edgeUpdate} /></div>
                        <div>P1 to P2&nbsp;<input type='radio' name='direction' id='wayone' value='str' checked={edgeType[1]} onChange={edgeUpdate} /></div>
                        <div>P2 to P1&nbsp;<input type='radio' name='direction' id='waytwo' value='inv' checked={edgeType[2]} onChange={edgeUpdate} /></div>
                        <div id='avoidance' style={{ marginTop: '10px' }}>Obstacle avoidance:&nbsp;<input type='checkbox' id='objAv' onChange={onChangeAvoid}></input></div>
                    </form>
                    <footer className='modal-card-foot'>
                        <button className='button is-success' onClick={unpopModal} style={{ marginRight: '10px' }}>
                            <span className="icon is-small">
                                <FontAwesomeIcon icon={faSave} className="is-small" />
                            </span>
                            <span>
                                Save
                            </span>
                        </button>
                        <button className='button is-danger' onClick={deleteEdge} style={{ marginRight: '0.5rem' }}>Delete</button>
                    </footer>
                </div>
            </div>

            {status === 'loaded' &&
                <Stage
                    id='stage'
                    ref={stageRef}
                    visible={stageVisibility}
                    width={document.getElementById('canvas-id')?.offsetWidth - 6}
                    height={document.getElementById('canvas-id')?.offsetHeight - 6}
                    draggable={context.selected.tool?._id === 'select' && !context.mappingStatus ? draggable : true}
                    onWheel={scrollZoom}
                    onTouchMove={!context.mappingStatus && context.selected.tool?._id === 'select' ? handleMouseMove : pinchZoom}
                    //onTouchEnd={pinchReset}
                    onTouchEnd={(e) => {
                        if (!context.mappingStatus) {
                            switch (context.selected.tool?._id) {
                                case 'zones':
                                    createZone(e)
                                    pinchReset(e)
                                    break

                                case 'points':
                                    createPoint(e)
                                    pinchReset(e)
                                    break

                                case 'select':
                                    handleMouseUp(e)
                                    if (context.pointClickState && context.selectedShapes.length === 0) unpopShapes()
                                    pinchReset(e)
                                    break

                                default:
                                    break
                            }
                        }
                    }}
                    onMouseUp={(e) => {
                        if (!context.mappingStatus) {
                            switch (context.selected.tool?._id) {
                                case 'zones':
                                    createZone(e)
                                    break

                                case 'points':
                                    createPoint(e)
                                    break

                                case 'select':
                                    handleMouseUp(e)
                                    if (context.pointClickState && context.selectedShapes.length === 0) unpopShapes()
                                    break

                                default:
                                    break
                            }
                        }
                    }}
                    onDragStart={!context.mappingStatus && handleOnDragStart}
                    onDragEnd={!context.mappingStatus && handleOnDragEnd}
                    style={{ borderStyle: 'solid' }}
                    //onTouchStart={shapeSelector}

                    //onKeyDown={(selectedShapes?.length > 0 || pointClickState) && hotkeyHandler}


                    onContextMenu={handleContextMenu}
                    onMouseDown={!context.mappingStatus && (context.selected.tool?._id === 'select' && handleMouseDown)}
                    onTouchStart={!context.mappingStatus && (context.selected.tool?._id === 'select' && handleMouseDown)}
                    onMouseMove={!context.mappingStatus && (context.selected.tool?._id === 'select' && handleMouseMove)}
                >
                    <Layer ref={imageLayerRef} listening={!context.mappingStatus}>
                        <Image
                            image={image}
                            onClick={!context.mappingStatus ? mapDetection : undefined}
                            onTouchEnd={!context.mappingStatus ? mapDetection : undefined}
                            ref={imageRef}
                        />
                    </Layer>


                    <Layer ref={layerRef}>
                        {!context.mappingStatus &&
                            <>
                                <Rect
                                    ref={selectionRectRef}
                                    draggable={true}
                                    fill="rgba(0,0,255,0.5)"
                                    visible={selectionBox.visible}
                                    x={selectionBox.x}
                                    y={selectionBox.y}
                                    width={selectionBox.width}
                                    height={selectionBox.height}
                                    listening={false}
                                />

                                <Transformer
                                    ref={(node) => {
                                        if (node) {
                                            if (context.selector) {
                                                const stage = node.getStage()
                                                //console.log(context.selectedRect)
                                                const rectangle = stage.findOne('.' + context.selectedRect.name)
                                                //console.log(rectangle)
                                                if (rectangle) node.nodes([rectangle])
                                                node.getLayer().batchDraw()
                                            } else {
                                                node.nodes([])
                                                node.getLayer().batchDraw()
                                            }
                                        }
                                    }}
                                    keepRatio={false}
                                    flipEnabled={false}
                                    rotateEnabled={true}
                                    onTransformEnd={updateZones}
                                    onMouseDown={() => console.log('transformer')}
                                //onClick={zoneClick}
                                //onTouchEnd={zoneClick}
                                />

                                {context.konvaZones.map((floor) => (
                                    <ZoneShape
                                        key={floor.name}
                                        shape={floor}
                                        onZoneClick={zoneClick}
                                        onDragStart={handleOnDragStart}
                                        onDragEnd={handleOnDragEnd}
                                    />
                                ))}

                                {context.konvaEdges.map((cons) => (
                                    <EdgeShape
                                        key={cons.name}
                                        shape={cons}
                                        onEdgeUp={context.selected.tool?._id !== 'points' ? edgeMenu : createPoint}
                                    />
                                ))}

                                {context.selected.tool?._id !== 'show' &&
                                    <Text
                                        visible={labelVisibility}
                                        x={pointLabel.x - 12}
                                        y={pointLabel.y - 16}
                                        fontFamily="NeoTechAlt"
                                        fontSize={10}
                                        fill='black'
                                        text={pointLabel.name}
                                    />
                                }

                                {context.konvaPoints.map((sky) => (
                                    <React.Fragment key={sky.name}>
                                        <PointShape
                                            shape={sky}
                                            onPointUp={pointClick}
                                            onTransformStart={handleOnDragStart}
                                            onTransformEnd={handleOnDragEnd}
                                        />

                                        {sky.fill === 'blue' && context.selected.tool?._id === 'show' &&
                                            <PointLabel shape={sky} visible={labelVisibility} />
                                        }
                                    </React.Fragment>
                                ))}

                                <Transformer ref={trRef} resizeEnabled={false} />
                            </>
                        }
                    </Layer>
                </Stage>
            }

            {context.lastMission === 'select-map' && !context.endOfMission &&
                <div id='loading' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', textAlign: 'center', fontSize: 30 }}>Loading map...</div>
            }
        </div>
    );
};